import { useAMCQueryData } from "../hooks/useAMCQueryData";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BASE_PATH_TO_PURCHASE_REPORT_CONFIG,
  CANNED_QUERIES,
  DEFAULT_LOOKBACK_PERIOD_IN_DAYS,
  FEATURED_REPORTS,
  normalizeQueryName,
} from "../utils/utils";
import { toTitleCase } from "../../../utils/formatText";
import { Link } from "@tanstack/react-router";
import { linkOptionsFromHref } from "../../../utils/searchParams";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { FaCog, FaTrash } from "react-icons/fa";
import { MdRotateLeft } from "react-icons/md";
import { Button, buttonRecipe } from "../../../components/core/basic/Button";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";
import { css } from "../../../../styled-system/css";
import { Switch } from "../../../components/fields/base/Switch";
import AMCReportsListing from "../../../views/amc/AMCReportsListing";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import TagsReviewDisplay from "../../../components/core/blocks/TagsReviewDisplay";
import { useSimpleCan } from "../../../config/casl/casl";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { commaSearchFilterFn } from "../../../components/table/filterFns";
import { resetTable } from "../../../components/table/utils";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";

const QUERY_SPECS = [...CANNED_QUERIES, ...FEATURED_REPORTS];

// The base Path to Purchase report has no subscription behind it, but is listed with the subscriptions.
const BASE_PATH_TO_PURCHASE_ROW = {
  ...FEATURED_REPORTS.find((report) => report.name === "PATH_TO_PURCHASE_GROUPS_INTERNAL"),
  ...BASE_PATH_TO_PURCHASE_REPORT_CONFIG,
};

/** The report type a row runs; the base Path to Purchase row has a spec name but no query type. */
const getQueryName = (row) => normalizeQueryName(row.query_type) ?? row.name;

const getQuerySpec = (row) => {
  const queryName = getQueryName(row);
  return QUERY_SPECS.find((querySpec) => querySpec.name === queryName);
};

const getLookbackDays = (row) => {
  // Legacy subscriptions predate a customisable lookback, so they run with the default.
  const fallback = getQuerySpec(row)?.parameter_list?.includes("days") ? DEFAULT_LOOKBACK_PERIOD_IN_DAYS : undefined;
  return row.parameters.days ?? fallback;
};

// The form saves a blank title as "", which would otherwise sort first and skip the "—" placeholder.
const getSubscriptionTitle = (row) => row.parameters.subscription_title?.trim() || undefined;

const columnHelper = createAppColumnHelper();

const canAlwaysExpand = () => true;

/** The table's column filters showing only one report type, for a "View subscriptions" link. */
export const filterToReportType = (reportType) => [{ id: "name", value: { op: "=", value: reportType } }];

// The dashboard owns the column filters, so its "View subscriptions" links can set them while this tab isn't mounted.
export const AMCSubscriptionsTable = ({
  columnFilters,
  onColumnFiltersChange,
  setSelectedQuery,
  setShowQueryForm,
  toggleStatusHandler,
  unsubscribeHandler,
}) => {
  const { t } = useTranslation(["amc", "common"]);
  const userCanUpdateSubscriptions = useSimpleCan("update", "AMC");
  const userCanDeleteSubscriptions = useSimpleCan("delete", "AMC");

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "amcSubscriptionsTable",
    // The dashboard owns the filters.
    skipPersisting: [
      "columnFilters",
      "columnPinning",
      "expanded",
      "globalFilter",
      "isAllMatchingSelected",
      "rowPinning",
      "rowSelection",
    ],
    initialState: {
      sorting: [{ id: "name", desc: false }],
      columnPinning: { start: ["name"], end: [] },
      pagination: { pageIndex: 0, pageSize: 5 },
    },
  });

  const { data: subscribedQueriesData, isLoading } = useAMCQueryData();

  const tableData = useMemo(
    () => subscribedQueriesData && [...subscribedQueriesData, BASE_PATH_TO_PURCHASE_ROW],
    [subscribedQueriesData],
  );

  const getDisplayName = useCallback(
    (row) => {
      const specDisplayName = getQuerySpec(row)?.display_name;
      if (specDisplayName) {
        return t(specDisplayName);
      }
      const queryName = getQueryName(row);
      return queryName ? toTitleCase(queryName.replaceAll("_", " ")) : undefined;
    },
    [t],
  );

  const globalFilterFn = useMemo(
    () => commaSearchFilterFn((row) => [getDisplayName(row) ?? "", getSubscriptionTitle(row) ?? ""]),
    [getDisplayName],
  );

  const reportTypeOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.amc["All Report Types"]) },
      ...QUERY_SPECS.map((querySpec) => ({ value: querySpec.name, label: t(querySpec.display_name) })),
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor(getDisplayName, {
        id: "name",
        header: t(($) => $.amc["Report Type"]),
        size: 220,
        enableHiding: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        // Matches the report type itself, which the filter options and "View subscriptions" links name.
        filterFn: (row, _columnId, { value }) => value === "all" || getQueryName(row.original) === value,
        cell: ({ getValue }) => <span className={css({ fontWeight: 500 })}>{getValue()}</span>,
        meta: { columnFilterOptions: { format: "single-select" } },
      }),
      columnHelper.accessor(getSubscriptionTitle, {
        id: "subscription_title",
        header: t(($) => $.amc["Title"]),
        size: 160,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        enableColumnFilter: false,
        cell: ({ getValue }) => getValue() ?? "—",
      }),
      columnHelper.display({
        id: "link",
        header: t(($) => $.amc["Link"]),
        size: 110,
        enableResizing: false,
        cell: ({ row }) => {
          const { id, isStatic, href } = row.original;
          return (
            <Link
              style={{ color: trellisPalette[13], fontWeight: 600 }}
              {...(isStatic ?
                linkOptionsFromHref(href)
              : { to: "/user/amc/dashboard/reports/$id", params: { id: String(id) } })}
            >
              {t(($) => $.amc["View Reports"])}
            </Link>
          );
        },
      }),
      columnHelper.accessor("frequency", {
        header: t(($) => $.amc["Frequency"]),
        size: 100,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const frequency = getValue();
          return (
            frequency === "daily" ? t(($) => $.amc["Daily"])
            : frequency === "weekly" ? t(($) => $.amc["Weekly"])
            : frequency === "monthly" ? t(($) => $.amc["Monthly"])
            : toTitleCase(frequency)
          );
        },
      }),
      columnHelper.accessor(getLookbackDays, {
        id: "lookback_days",
        header: t(($) => $.amc["Lookback Period"]),
        size: 150,
        enableResizing: false,
        sortUndefined: "last",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const days = getValue();
          return days == null ? null : t(($) => $.amc["{count} Days"], { count: days });
        },
        meta: { justify: "end" },
      }),
      columnHelper.accessor("parameters.tag", {
        header: t(($) => $.amc["Tags"]),
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ getValue }) => <TagsReviewDisplay tags={getValue() ?? null} />,
      }),
      columnHelper.display({
        id: "actions",
        header: t(($) => $.amc["Actions"]),
        size: 120,
        enableResizing: false,
        cell: ({ row }) => {
          const subscription = row.original;
          const displayName = row.getValue("name");
          if (subscription.isStatic) {
            return (
              <HoverInfo iconCss={{ color: "gray" }}>
                {t(($) => $.amc["Actions are unavailable for the base version of featured reports."])}
              </HoverInfo>
            );
          }
          return (
            <Flex alignItems="center" gap="medium">
              <Button
                variant={"contentOnly"}
                onPress={() => {
                  setSelectedQuery({
                    ...getQuerySpec(subscription),
                    ...subscription,
                    display_name: displayName,
                    subscribed: true,
                  });
                  setShowQueryForm(true);
                }}
                isDisabled={!userCanUpdateSubscriptions}
              >
                <FaCog className={css({ color: "#777", fontSize: "large" })} />
              </Button>

              {userCanDeleteSubscriptions && (
                <ConfirmationModal
                  title={t(($) => $.amc["Confirm Unsubscription"])}
                  confirmButtonText={t(($) => $.amc["Confirm"])}
                  confirmationMessage={
                    <p className="fs-4 w-75 mx-auto pt-0 pb-4">
                      {t(
                        ($) =>
                          $.amc[
                            "Are you sure you want to unsubscribe from the {displayName} query? If you continue, reports will no longer be generated for this query effective immediately."
                          ],
                        { displayName },
                      )}
                    </p>
                  }
                  onConfirm={() => {
                    unsubscribeHandler.mutate(subscription.id);
                  }}
                  inlineModal={true}
                  inlineButtonContainerStyles={{}}
                  modalButtonText={
                    <button className={buttonRecipe({ variant: "contentOnly" })}>
                      <FaTrash className={css({ color: "#777", fontSize: "large" })} />
                    </button>
                  }
                />
              )}
              <div className={css({ transform: "scale(.75)" })}>
                <Switch
                  isSelected={subscription.enabled}
                  onChange={(isSelected) => {
                    toggleStatusHandler.mutate({
                      subscriptionId: subscription.id,
                      enabled: isSelected,
                      queryName: displayName,
                    });
                  }}
                  isDisabled={!userCanUpdateSubscriptions}
                />
              </div>
            </Flex>
          );
        },
      }),
    ],
    [
      getDisplayName,
      setSelectedQuery,
      setShowQueryForm,
      toggleStatusHandler,
      unsubscribeHandler,
      userCanDeleteSubscriptions,
      userCanUpdateSubscriptions,
      t,
    ],
  );

  const table = useClientTable({
    data: tableData,
    columns,
    getRowId: (row) => String(row.id),
    getRowCanExpand: canAlwaysExpand,
    globalFilterFn,
    initialState,
    state: { ...state, columnFilters },
    ...setters,
    onColumnFiltersChange,
    meta: {
      showSkeleton: isLoading,
      columnFilterSelectOptions: { name: reportTypeOptions },
    },
  });

  return (
    <>
      <h3 className="pb-3 fs-2 my-0" style={{ fontWeight: 600 }}>
        {t(($) => $.amc["Subscriptions"])}
      </h3>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        renderExpander={(row) => <AMCReportsListing fullPage={false} overrideId={row.original.id} />}
      />
    </>
  );
};
